// Ref: https://chakra-ui.com/docs/components/switch/theming#adding-a-custom-variant

import { switchAnatomy as parts } from '@chakra-ui/anatomy';
import {
    createMultiStyleConfigHelpers,
    defineStyle,
    defineStyleConfig,
} from '@chakra-ui/styled-system';
import { calc, cssVar } from '@chakra-ui/theme-tools';

const { definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

const $width = cssVar('switch-track-width');
const $height = cssVar('switch-track-height');
const $diff = cssVar('switch-track-diff');
const diffValue = calc.subtract($width, $height);
const $translateX = cssVar('switch-thumb-x');
const $bg = cssVar('switch-bg');

// @ts-expect-error TODO: Fix typing
const baseStyle: SystemStyleObject = definePartsStyle((props) => {
    const { colorScheme: c } = props;
    return {
        container: {
            [$diff.variable]: diffValue,
            [$translateX.variable]: $diff.reference,
            _rtl: {
                [$translateX.variable]: calc($diff).negate().toString(),
            },
        },
        track: {
            borderRadius: 'full',
            p: '0.5',
            width: [$width.reference],
            height: [$height.reference],
            transitionProperty: 'common',
            transitionDuration: 'fast',
            [$bg.variable]: 'colors.gray.500',
            _dark: {
                [$bg.variable]: 'colors.whiteAlpha.400',
            },
            _focusVisible: {
                boxShadow: 'outline',
            },
            _disabled: {
                opacity: 0.4,
                cursor: 'not-allowed',
            },
            _checked: {
                [$bg.variable]: `colors.${c}.500`,
                _dark: {
                    [$bg.variable]: `colors.${c}.200`,
                },
            },
            bg: $bg.reference,
        },
        thumb: {
            bg: 'white',
            transitionProperty: 'transform',
            transitionDuration: 'normal',
            borderRadius: 'inherit',
            width: [$height.reference],
            height: [$height.reference],
            _checked: {
                transform: `translateX(${$translateX.reference})`,
                [$bg.variable]: `colors.${c}.300`,
            },
        },
    };
});

const variantYesNo = defineStyle(() => {
    const labelStyle = {
        position: 'absolute',
        fontWeight: 500,
        top: '50%',
        transform: 'translate3d(-50%, -50%, 0)',
    };
    return {
        thumb: {
            width: '50%',
            height: '100%',
            zIndex: 2,
            borderRadius: '6px',
            position: 'relative',
            transform: 'translate3d(100%, 0, 0)',
            _before: {
                content: '"No"',
                left: '50%',
                ...labelStyle,
            },
            _hover: {
                transform: 'translate3d(95%, 0, 0)',
            },
            _checked: {
                transform: 'translate3d(0%, 0, 0)',
                _before: {
                    content: '"Yes"',
                },
                _hover: {
                    transform: 'translate3d(5%, 0, 0)',
                },
            },
        },
        track: {
            width: '120px',
            height: 10,
            borderRadius: '6px',
            position: 'relative',
            bg: 'gray.200',
            _before: {
                content: '"Yes"',
                left: '25%',
                opacity: 0.25,
                ...labelStyle,
            },
            _after: {
                content: '"No"',
                left: '75%',
                opacity: 0.25,
                ...labelStyle,
            },
            _checked: {
                bg: 'gray.200',
            },
        },
    };
});

const variantDisco = defineStyle(() => {
    return {
        thumb: {
            width: '22px',
            height: '22px',
            zIndex: 2,
            borderRadius: '50%',
            position: 'absolute',
            top: '50%',
            left: '25%',
            transform: 'translate3d(-50%, -50%, 0)',
            transition: 'all 150ms ease-in-out',
            _checked: {
                left: '75%',
                transform: 'translate3d(-50%, -50%, 0)',
            },
        },
        track: {
            width: '54px',
            height: '24px',
            borderRadius: '24px',
            position: 'relative',
            bg: 'gray.300',
            overflow: 'hidden',
            _before: {
                content: '"Yes \\00a0 \\00a0 No"',
                left: '50%',
                width: '100%',
                color: 'white',
                position: 'absolute',
                fontWeight: 600,
                fontSize: 'sm',
                textAlign: 'center',
                top: '50%',
                transform: 'translate3d(-54%, -50%, 0)',
                transition: 'all 150ms ease-in-out',
                zIndex: 2,
            },
            _after: {
                content: '""',
                bg: 'linear-gradient(45deg, #49ADE4 0%, #34B941 40%, #F3B71C 80%, #FF9737 100%)',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                opacity: 0,
                transition: 'all 200ms ease-out',
            },
            _checked: {
                bg: 'gray.300',
                _before: {
                    transform: 'translate3d(-45%, -50%, 0)',
                },
                _after: {
                    opacity: 1,
                },
            },
        },
    };
});

const switchTheme = defineStyleConfig({
    variants: {
        base: baseStyle,
        yesNo: variantYesNo,
        disco: variantDisco,
    },
    defaultProps: {
        variant: 'base',
    },
});

export default switchTheme;
